/**
 * MainMenuScene - Main lobby with starfield background
 * Features: Centered logo image, Play Game button
 */

import Phaser from 'phaser';
import { I18n } from '../../i18n/I18nService';

export class MainMenuScene extends Phaser.Scene {
  private activeTweens: Phaser.Tweens.Tween[] = [];

  constructor() {
    super({ key: 'MainMenuScene' });
  }

  create(): void {
    const { width, height } = this.cameras.main;

    // Create space background
    this.createSpaceBackground(width, height);

    // Create centered circular logo
    this.createCircularLogo(width, height);

    // Create left-side vertical menu
    this.createVerticalMenu(width, height);

    // Version info
    this.add
      .text(width / 2, height - 20, I18n.t('menu.version'), {
        fontSize: '11px',
        color: '#5a6a8a',
        fontFamily: 'Arial',
        letterSpacing: 1,
      })
      .setOrigin(0.5)
      .setAlpha(0.7);

    // Register shutdown event
    this.events.once('shutdown', this.cleanup, this);
  }

  /**
   * Create deep space background
   */
  private createSpaceBackground(width: number, height: number): void {
    // Starfield background image
    if (this.textures.exists('bg-starfield')) {
      const bg = this.add.image(width / 2, height / 2, 'bg-starfield');
      bg.setDisplaySize(width, height);
    }
  }

  /**
   * Create centered logo image
   */
  private createCircularLogo(width: number, height: number): void {
    const centerX = width / 2;
    const centerY = height / 2 - 50;

    // Logo image
    if (this.textures.exists('logo')) {
      const logo = this.add.image(centerX, centerY, 'logo');
      const maxWidth = Math.min(550, width - 60);
      const scale = maxWidth / logo.width;
      logo.setScale(Math.min(scale, 1));

      // Subtle pulse animation
      this.activeTweens.push(
        this.tweens.add({
          targets: logo,
          alpha: { from: 0.9, to: 1 },
          duration: 2000,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        }),
      );
    }
  }

  /**
   * Create single centered Play Game button
   */
  private createVerticalMenu(width: number, height: number): void {
    const centerX = width / 2;
    const compact = width < 600 || height < 700;
    const preferredY = compact ? height * 0.76 : height / 2 + 200;
    const bottomLimit = Math.max(220, height - (compact ? 70 : 95));
    const centerY = Math.min(preferredY, bottomLimit);

    // Create large centered play button
    this.createPlayButton(centerX, centerY);
  }

  /**
   * Create large centered Play Game button
   */
  private createPlayButton(x: number, y: number): void {
    const container = this.add.container(x, y);
    container.setDepth(10);

    // Background
    const viewportWidth = this.cameras.main.width;
    const viewportHeight = this.cameras.main.height;
    const compact = viewportWidth < 600 || viewportHeight < 700;
    const sideGutter = compact ? 32 : 80;
    const buttonWidth = Math.max(220, Math.min(compact ? 320 : 400, viewportWidth - sideGutter));
    const buttonHeight = compact ? 64 : 80;
    const bg = this.add.rectangle(0, 0, buttonWidth, buttonHeight, 0x003366);
    bg.setStrokeStyle(compact ? 2 : 3, 0x0066cc);

    // Glow effect
    const glow = this.add.rectangle(0, 0, buttonWidth, buttonHeight, 0x4488cc, 0);
    glow.setBlendMode(Phaser.BlendModes.ADD);

    // Text
    const text = this.add
      .text(0, 0, I18n.t('menu.play_game'), {
        fontSize: compact ? '24px' : '36px',
        color: '#ffffff',
        fontFamily: 'Arial',
        fontStyle: 'bold',
        letterSpacing: compact ? 2 : 4,
      })
      .setOrigin(0.5);

    container.add([glow, bg, text]);

    // Make interactive - use full rectangle with padding for better hit detection
    container.setSize(buttonWidth + 20, buttonHeight + 20);
    const hitArea = new Phaser.Geom.Rectangle(
      -(buttonWidth + 20) / 2,
      -(buttonHeight + 20) / 2,
      buttonWidth + 20,
      buttonHeight + 20,
    );
    container.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    if (container.input) {
      container.input.cursor = 'pointer';
    }

    // Pulse animation
    this.activeTweens.push(
      this.tweens.add({
        targets: glow,
        alpha: { from: 0, to: 0.3 },
        duration: 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      }),
    );

    // Hover effects
    container.on('pointerover', () => {
      this.input.setDefaultCursor('pointer');

      this.activeTweens.push(
        this.tweens.add({
          targets: bg,
          fillColor: 0x004488,
          duration: 200,
          ease: 'Cubic.easeOut',
        }),
      );

      this.activeTweens.push(
        this.tweens.add({
          targets: container,
          scale: 1.05,
          duration: 200,
          ease: 'Back.easeOut',
        }),
      );
    });

    container.on('pointerout', () => {
      this.input.setDefaultCursor('default');

      this.activeTweens.push(
        this.tweens.add({
          targets: bg,
          fillColor: 0x003366,
          duration: 200,
          ease: 'Cubic.easeOut',
        }),
      );

      this.activeTweens.push(
        this.tweens.add({
          targets: container,
          scale: 1,
          duration: 200,
          ease: 'Cubic.easeOut',
        }),
      );
    });

    container.on('pointerdown', () => {
      this.activeTweens.push(
        this.tweens.add({
          targets: container,
          scale: 0.95,
          duration: 100,
          ease: 'Cubic.easeOut',
        }),
      );
    });

    container.on('pointerup', () => {
      this.activeTweens.push(
        this.tweens.add({
          targets: container,
          scale: 1,
          duration: 150,
          ease: 'Back.easeOut',
        }),
      );

      // Go directly to MMO login
      this.scene.start('MMOLoginScene');
    });

    this.add.existing(container);
  }

  /**
   * Cleanup animations and tweens
   */
  private cleanup(): void {
    // Stop all active tweens
    for (const tween of this.activeTweens) {
      if (tween && tween.isPlaying()) {
        tween.stop();
      }
    }
    this.activeTweens = [];
  }

  shutdown(): void {
    this.cleanup();
  }
}
